import {
  LADO_MAP,
  TIPO_MAP,
  TX_TIPO_MAP,
  type MovimentoConciliado,
} from "../../lib/conciliacao/tipos";
import { formatarDataISO } from "../../lib/formato/data";
import { formatarDecimal } from "../../lib/formato/decimal";
import { formatarScore } from "./score";

/*
 * Card de um movimento JA conciliado (FE-4 — Tela 4 §10.5, LEITURA + acao).
 *
 * Mostra a TRANSACAO bancaria (esquerda) ⟷ o TITULO casado (direita), lado a lado,
 * com um separador `→` em `text-flame` entre os blocos. Espelha o estilo do item
 * de `FilaConciliacao` (dl grid-cols-[auto_1fr], dt `font-subtitulo`).
 *
 * Os rotulos do wire (`tipo`/`transacao.tipo`/`titulo.lado` lowercase) sao
 * traduzidos para PT-BR via os mapas `as const` (`TIPO_MAP`/`TX_TIPO_MAP`/
 * `LADO_MAP`) com FALLBACK ao valor cru — se o backend introduzir um valor novo, a
 * UI exibe o valor original em vez de quebrar.
 *
 * `cnpj_masc` JA vem mascarado do backend (ou `null` → "—"). Valores monetarios
 * via `formatarDecimal` (string-based, sem "R$"); datas via `formatarDataISO`.
 *
 * O botao "Desfazer" so renderiza com a permissao `desfazer_conciliacao` — o
 * componente NAO chama o hook (nao pode, vive sob `.map`): recebe `podeDesfazer`
 * como prop, resolvido pela pagina.
 */

const SEM_VALOR = "—";

/** Consulta um mapa de rotulos `as const`, com fallback ao valor cru. */
function rotular(
  mapa: Record<string, string>,
  chave: string,
): string {
  return mapa[chave] ?? chave;
}

interface CardMovimentoConciliadoProps {
  movimento: MovimentoConciliado;
  /** Abre o passo de desfazer para este movimento. */
  aoDesfazer: (movimento: MovimentoConciliado) => void;
  /** Permissao `desfazer_conciliacao` — sem ela o botao nao renderiza. */
  podeDesfazer: boolean;
}

export function CardMovimentoConciliado({
  movimento,
  aoDesfazer,
  podeDesfazer,
}: CardMovimentoConciliadoProps): JSX.Element {
  const { transacao, titulo } = movimento;

  return (
    <li className="rounded-lg border border-platinum bg-snow p-4 font-corpo text-base text-off-black">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-8">
        <div className="flex flex-col gap-1">
          <h3 className="font-subtitulo">Transação</h3>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4">
            <dt className="font-subtitulo text-off-black">Valor</dt>
            <dd>{formatarDecimal(transacao.valor)}</dd>
            <dt className="font-subtitulo text-off-black">Data</dt>
            <dd>{formatarDataISO(transacao.data)}</dd>
            <dt className="font-subtitulo text-off-black">Tipo</dt>
            <dd>{rotular(TX_TIPO_MAP, transacao.tipo)}</dd>
            <dt className="font-subtitulo text-off-black">CNPJ</dt>
            <dd>{transacao.cnpj_masc ?? SEM_VALOR}</dd>
            <dt className="font-subtitulo text-off-black">Descrição</dt>
            <dd>{transacao.descricao ?? SEM_VALOR}</dd>
          </dl>
        </div>

        <span aria-hidden="true" className="text-flame">
          →
        </span>

        <div className="flex flex-col gap-1">
          <h3 className="font-subtitulo">Título</h3>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4">
            <dt className="font-subtitulo text-off-black">Lado</dt>
            <dd>
              <span className="rounded-md border border-platinum bg-platinum px-2 py-0.5 text-off-black">
                {rotular(LADO_MAP, titulo.lado)}
              </span>
            </dd>
            <dt className="font-subtitulo text-off-black">Valor</dt>
            <dd>{formatarDecimal(titulo.valor)}</dd>
            <dt className="font-subtitulo text-off-black">Data prevista</dt>
            <dd>{formatarDataISO(titulo.data_prevista)}</dd>
            <dt className="font-subtitulo text-off-black">CNPJ</dt>
            <dd>{titulo.cnpj_masc ?? SEM_VALOR}</dd>
          </dl>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span>
          <span className="font-subtitulo">Tipo: </span>
          {rotular(TIPO_MAP, movimento.tipo)}
        </span>
        <span>
          <span className="font-subtitulo">Score: </span>
          {movimento.score === null ? SEM_VALOR : formatarScore(movimento.score)}
        </span>
        {podeDesfazer && (
          <button
            type="button"
            onClick={() => aoDesfazer(movimento)}
            className="rounded-md border border-flame bg-flame px-3 py-1 text-snow"
          >
            Desfazer
          </button>
        )}
      </div>
    </li>
  );
}
